import { watch, onBeforeUnmount, unref } from 'vue'
import { onBeforeRouteLeave } from 'vue-router'

import { useConfirm } from './useConfirm'

/**
 * Stops a half-filled form from evaporating.
 *
 * An ERP form is long — a company profile has twenty fields — and losing it to
 * a stray click on the sidebar is the kind of small betrayal that makes people
 * distrust the whole tool. This guards both ways out: navigating inside the SPA
 * (a dialog they can cancel) and closing the tab (the browser's own prompt,
 * which is all a page is allowed to do there).
 *
 *   useUnsavedChanges(() => dirty.value, {
 *     title: t('common.unsavedTitle'),
 *     message: t('common.unsavedMessage'),
 *     confirmLabel: t('common.discard'),
 *     cancelLabel: t('common.keepEditing'),
 *   })
 *
 * @param {Function|import('vue').Ref} isDirty
 * @param {object} texts  already translated by the caller — the ui-kit has no
 *        i18n of its own on purpose, so it never has to ship translations
 */
/**
 * Los formularios con cambios sin guardar que hay montados ahora mismo.
 *
 * Para quien recarga la página sin navegar (tirar hacia abajo para recargar,
 * en el teléfono): tiene que poder preguntar «¿descartar?» con las palabras
 * del formulario antes de llamar a `location.reload()`, que el router no ve.
 */
const guards = new Set()

/** Tras «Descartar» ya se ha preguntado: el aviso propio del navegador sobra. */
let discarding = false

/**
 * Pregunta si se descartan los cambios de cualquier formulario abierto.
 * `true` si no hay nada que perder o si la persona lo descarta; entonces el
 * `beforeunload` de esos formularios deja de preguntar otra vez.
 */
export async function confirmDiscardingUnsaved() {
  const open = [...guards].find(guard => guard.dirty())
  if (!open) return true
  const yes = await open.ask()
  if (yes) discarding = true
  return yes
}

export function useUnsavedChanges(isDirty, texts = {}) {
  const { confirm } = useConfirm()
  const dirty = () => Boolean(typeof isDirty === 'function' ? isDirty() : unref(isDirty))
  const ask = () => confirm({
    title: texts.title ?? 'Discard changes?',
    message: texts.message ?? 'What you have edited will be lost.',
    confirmLabel: texts.confirmLabel ?? 'Discard',
    cancelLabel: texts.cancelLabel ?? 'Keep editing',
    danger: true,
  })
  const guard = { dirty, ask }
  guards.add(guard)

  // ── Closing the tab ────────────────────────────────────────────────────────
  // The browser shows its own wording; ours is ignored by every modern engine.
  // The listener is only attached while there is something to lose, so a clean
  // form never costs the user an extra click.
  function onBeforeUnload(event) {
    if (discarding) return
    event.preventDefault()
    event.returnValue = ''
  }

  const stopWatching = watch(dirty, (value) => {
    if (value) window.addEventListener('beforeunload', onBeforeUnload)
    else window.removeEventListener('beforeunload', onBeforeUnload)
  }, { immediate: true })

  onBeforeUnmount(() => {
    stopWatching()
    guards.delete(guard)
    window.removeEventListener('beforeunload', onBeforeUnload)
  })

  // ── Navigating inside the app ──────────────────────────────────────────────
  onBeforeRouteLeave(async () => {
    if (!dirty()) return true
    return ask()
  })
}

export default useUnsavedChanges
